import { inject, Injectable } from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
    AbstractControl,
    FormArray,
} from '@angular/forms';
import { LogicStepParameter, LogicStepParameterType } from '@wdx/shared/utils';
import { v4 as uuidv4 } from 'uuid';
import {
    ExtendedPrimitiveKinds,
    LookupKinds,
    SegmentedKinds,
} from '../utils/logic-step-parameter-kinds';

@Injectable({ providedIn: 'root' })
export class WorkflowParameterFormService {
    private readonly fb = inject(FormBuilder);

    buildFormGroup(
        parameters: LogicStepParameter[],
        parentValues: Record<string, any>,
    ): FormGroup {
        const group: { [key: string]: AbstractControl } = {};

        if (parameters) {
            for (const param of parameters) {
                if (param.kind === LogicStepParameterType.LogicStep) {
                    continue;
                }

                group[param.code] = this.buildControlForParameter(
                    param,
                    parentValues,
                );
            }
        }

        return this.fb.group(group);
    }

    buildControlForParameter(
        param: LogicStepParameter,
        parentValues: Record<string, any>,
    ): AbstractControl {
        const isRequired = param.isRequired ?? false;
        const validators = isRequired ? [Validators.required] : [];
        const key = Object.keys(parentValues ?? {}).find(
            (k) => k.toLowerCase() === param.code.toLowerCase(),
        );
        const value = key ? parentValues?.[key] : null;

        const isSimpleControlKind = ExtendedPrimitiveKinds.includes(param.kind);

        if (isSimpleControlKind && !param.isArray) {
            return new FormControl(value, validators);
        } else if (SegmentedKinds.includes(param.kind)) {
            return this.buildFormGroup(param.properties, value ?? {});
        } else if (LookupKinds.includes(param.kind) || param.options) {
            const matchedValue =
                param.options?.find(
                    (option) => String(option.value) === String(value),
                )?.value ?? value;
            return new FormControl(
                param.isArray
                    ? value
                        ? Array.isArray(value)
                            ? value
                            : [value]
                        : []
                    : matchedValue,
                validators,
            );
        }

        if (param.isArray || param.kind === LogicStepParameterType.Object) {
            const arrayValues = Array.isArray(value) ? value : [value];

            const itemGroups = arrayValues.map((arrayVal) =>
                this.buildCollectionItemGroup(param, arrayVal),
            );

            return this.fb.array(itemGroups);
        }

        return new FormControl(null);
    }

    transformSavedValues(
        rawValue: any,
        parameters: LogicStepParameter[],
    ): Record<string, any> {
        const result: Record<string, any> = {};

        Object.entries(rawValue).forEach(([key, value]) => {
            if (key === 'id') {
                return;
            }

            const param = parameters?.find((p) => p.code === key);

            if (key === 'Conditions' && Array.isArray(value)) {
                result[key] = this.mapConditions(value);
                return;
            }

            if (key === 'query') {
                result[key] = JSON.parse(value as string);
                return;
            }

            if (key === 'Operator' && param?.options?.length) {
                result[key] = this.mapOperatorValue(value, param);
                return;
            }

            if (
                (key === 'Value' ||
                    param?.kind === ('Reference' as LogicStepParameterType)) &&
                this.isLookupObject(value)
            ) {
                result[key] = value.id;
                return;
            }

            if (param?.isArray && Array.isArray(value)) {
                result[key] = value.map((item) => item[key] ?? item);
                return;
            }

            result[key] = value;
        });

        return result;
    }

    addCollectionItem(array: FormArray, param: LogicStepParameter): void {
        array.push(this.buildCollectionItemGroup(param));
    }

    removeCollectionItem(array: FormArray, index: number): void {
        if (array.length > 1) {
            array.removeAt(index);
        }
    }

    private buildCollectionItemGroup(
        param: LogicStepParameter,
        value: any = null,
    ): FormGroup {
        const isRequired = param.isRequired ?? false;
        const validators = isRequired ? [Validators.required] : [];

        let group: FormGroup;

        if (param.kind === LogicStepParameterType.Object && param.properties) {
            group = this.buildFormGroup(param.properties, value ?? {});
        } else {
            group = this.fb.group({
                [param.code]: new FormControl(value, validators),
            });
        }

        group.addControl('id', new FormControl(uuidv4()));
        return group;
    }

    private mapConditions(conditions: any[]): any[] {
        return conditions.map((condition) => {
            if (
                condition &&
                typeof condition === 'object' &&
                this.isLookupObject(condition.Value)
            ) {
                return {
                    ...condition,
                    Value: condition.Value.id,
                };
            }
            return condition;
        });
    }

    private mapOperatorValue(value: any, param?: LogicStepParameter) {
        const match = param?.options?.find((opt) => opt.label === value);
        return match?.value ?? value;
    }

    private isLookupObject(
        value: unknown,
    ): value is { id: unknown; lookupType: unknown } {
        return (
            !!value &&
            typeof value === 'object' &&
            'id' in value &&
            'lookupType' in value
        );
    }
}
